/**
 * Offline POS — shared types (Electron kiosk local mirror + sync queue).
 */

export type OfflineFeature =
  | 'sale'
  | 'refund'
  | 'catalog'
  | 'inventaire'
  | 'cloture'
  | 'profile';

export type PendingOpKind = 'sale' | 'refund';

export interface OfflineProduct {
  visibleId: string;
  name: string;
  sku: string;
  barcode?: string | null;
  description?: string | null;
  salePriceHT: number;
  taxRate: number;
  categoryId: string;
  isActive: boolean;
}

export interface OfflineInventoryRow {
  productId: string;
  branchId: string;
  stock: number;
  exposedOnSite?: number;
}

export interface OfflineCatalogSnapshot {
  branchId: string;
  syncedAt: string; // ISO
  products: OfflineProduct[];
  inventories: OfflineInventoryRow[];
  categories: Array<{
    visibleId: string;
    name: string;
    isActive: boolean;
    isFreeSaleOrService?: boolean;
    isFreePrice?: boolean;
  }>;
}

export interface OfflineSaleItem {
  productId: string;
  productName: string;
  unitPrice: number;
  taxRate: number;
  quantity: number;
  subtotal: number;
  productDiscountPercentage: number;
  productDiscountAmount: number;
  isFreePrice?: boolean;
  categoryId?: string;
  /** Units already returned or exchanged. Stays on the local receipt only. */
  returnedQuantity?: number;
}

export interface OfflineSalePayload {
  cartId: string;
  cashierId: string;
  branchId: string;
  items: OfflineSaleItem[];
  discountPercentage: number;
  subtotal: number;
  discountAmount: number;
  totalAmount: number;
  payment: {
    method: string;
    amountPaid: number;
    cashGiven?: number;
    cashReturned?: number;
  };
  shouldPrintReceipt: boolean;
}

export interface OfflineRefundExchange {
  originalProductId: string;
  replacementProductId: string;
  replacementName: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;
}

export interface OfflineRefundPayload {
  originalSaleId: string;
  originalReceiptId: string;
  cashierId: string;
  branchId: string;
  items: OfflineSaleItem[];
  refundAmount: number;
  collectedAmount?: number;
  exchanges?: OfflineRefundExchange[];
  /** False while units remain on the original receipt. */
  settled?: boolean;
  reason?: string;
}

export interface PendingOp {
  clientOpId: string;
  kind: PendingOpKind;
  createdAt: string;
  payload: OfflineSalePayload | OfflineRefundPayload;
  attempts: number;
  lastError?: string;
  /** Local receipt id shown to cashier before server assigns one */
  localReceiptId?: string;
}

export interface OfflineBranch {
  visibleId: string;
  name: string;
  code: string;
  address?: string;
  isMainBranch?: boolean;
}

export interface OfflineUserSnapshot {
  visibleId: string;
  username: string;
  firstName: string;
  lastName: string;
  role: string;
  branchId: string;
  email?: string;
}

export interface OfflineAuthProfile {
  username: string;
  branchId: string;
  branchName?: string;
  user: OfflineUserSnapshot;
  /** Opaque verifier — never plaintext password */
  credentialVerifier: string;
  encryptedRefreshToken?: string;
  lastOnlineLoginAt: string;
}

export interface OfflineLoginCredentials {
  username: string;
  password: string;
  branchId: string;
}

export type OfflineReceiptStatus = 'COMPLETED' | 'PARTIAL' | 'REFUNDED';

/** Cached sales rows for Historique / refund lookup when offline. */
export interface OfflineReceiptHistoryEntry {
  id: string;
  saleVisibleId: string;
  receiptNumber: string;
  cartId: string;
  branchId: string;
  cashierName: string;
  items: OfflineSaleItem[];
  subtotal: number;
  discountPercentage: number;
  discountAmount: number;
  grandTotal: number;
  paymentMethod: string;
  cashGiven: number;
  cashReturned: number;
  createdAt: string;
  status: OfflineReceiptStatus;
  /** True until backend ack assigns RCP-* receipt id */
  pendingSync?: boolean;
}

export interface OfflinePosPersistedState {
  version: 2;
  catalog: OfflineCatalogSnapshot | null;
  pending: PendingOp[];
  branches: OfflineBranch[];
  authProfiles: OfflineAuthProfile[];
  salesHistory: OfflineReceiptHistoryEntry[];
  lastOnlineAt: string | null;
  lastSyncAt: string | null;
}

/** @deprecated v1 shape — migrated to v2 on read */
export interface OfflinePosPersistedStateV1 {
  version: 1;
  catalog: OfflineCatalogSnapshot | null;
  pending: PendingOp[];
  lastOnlineAt: string | null;
  lastSyncAt: string | null;
}

export const OFFLINE_POS_STORAGE_KEY = 'incashflow_offline_pos_v1';

export interface OfflineKvStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
}

export interface SyncHttpClient {
  getJson(path: string, accessToken: string): Promise<unknown>;
  postJson(path: string, accessToken: string, body: unknown): Promise<{ ok: boolean; status: number; data: unknown }>;
}

export const ONLINE_ONLY_FEATURES: readonly OfflineFeature[] = [
  'inventaire',
  'cloture',
  'profile',
] as const;

export const ONLINE_ONLY_MESSAGES: Record<
  'inventaire' | 'cloture' | 'profile' | 'first_launch' | 'offline_login_unavailable',
  string
> = {
  inventaire:
    'Inventaire nécessite une connexion Internet. Reconnectez-vous pour continuer.',
  cloture:
    'La clôture de caisse nécessite une connexion Internet. Reconnectez-vous pour continuer.',
  profile:
    'La mise à jour du profil nécessite une connexion Internet. Reconnectez-vous pour continuer.',
  first_launch:
    'Aucune donnée locale n’a encore été synchronisée. Connectez-vous à Internet au moins une fois pour télécharger le catalogue et le stock avant d’utiliser la caisse hors ligne.',
  offline_login_unavailable:
    'Connexion hors ligne indisponible. Connectez-vous une fois en ligne avec « Se souvenir de moi » pour activer la reconnexion hors ligne.',
};
